import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "src/services/apiClient";

export type DraftSaveStatus = "idle" | "saving" | "saved" | "error";

interface Snapshot {
    step: number;
    data: unknown;
    summary: { patientName: string; total: number; lineCount: number };
}

const DEBOUNCE_MS = 1000;
const json = { "Content-Type": "application/json" };

/**
 * Saves the creation page as a draft (/api/invoice-drafts) about a second after the last change.
 * - The first save creates the draft (POST) and reports its id via `onCreated`; later saves PUT.
 * - Never two requests at once: a change during a save is saved right after it, so the first
 *   save can't create the draft twice.
 * - `stop()` before creating the invoice (the server deletes the draft then — nothing may write
 *   it again); `adopt()` after resuming a draft, so loading it doesn't count as a change.
 * - Leaving the page saves what's pending once more (best effort).
 */
export function useDraftAutosave({
    snapshot,
    shouldSave,
    onCreated,
}: {
    snapshot: Snapshot;
    /** False until something worth keeping was entered (or while a draft is still loading). */
    shouldSave: boolean;
    onCreated: (id: string) => void;
}) {
    const [status, setStatus] = useState<DraftSaveStatus>("idle");
    const [savedAt, setSavedAt] = useState<Date | null>(null);
    const [draftId, setDraftId] = useState<string | null>(null);

    const idRef = useRef<string | null>(null);
    const latest = useRef(snapshot);
    const savedKey = useRef<string | null>(null);
    const inFlight = useRef(false);
    const inFlightDone = useRef<Promise<void>>(Promise.resolve());
    const pending = useRef(false);
    const stopped = useRef(false);
    const onCreatedRef = useRef(onCreated);
    onCreatedRef.current = onCreated;

    const key = JSON.stringify(snapshot);
    latest.current = snapshot;

    const save = useCallback(async () => {
        if (stopped.current) return;
        if (inFlight.current) {
            pending.current = true;
            return;
        }
        const body = latest.current;
        const bodyKey = JSON.stringify(body);
        if (bodyKey === savedKey.current) return;

        inFlight.current = true;
        let finish!: () => void;
        inFlightDone.current = new Promise<void>((resolve) => (finish = resolve));
        setStatus("saving");
        try {
            if (idRef.current) {
                await apiRequest(`/invoice-drafts/${idRef.current}`, { method: "PUT", body: bodyKey }, json);
            } else {
                const created = await apiRequest<{ id: string }>("/invoice-drafts", { method: "POST", body: bodyKey }, json);
                idRef.current = created.id;
                setDraftId(created.id);
                onCreatedRef.current(created.id);
            }
            savedKey.current = bodyKey;
            setSavedAt(new Date());
            setStatus("saved");
        } catch {
            setStatus("error");
        } finally {
            inFlight.current = false;
            finish();
            if (pending.current) {
                pending.current = false;
                void save();
            }
        }
    }, []);

    useEffect(() => {
        if (!shouldSave || stopped.current || key === savedKey.current) return;
        const timer = window.setTimeout(() => void save(), DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [key, shouldSave, save]);

    // Leaving the page: save what's still pending (fire and forget).
    const shouldSaveRef = useRef(shouldSave);
    shouldSaveRef.current = shouldSave;
    useEffect(() => () => {
        if (shouldSaveRef.current && JSON.stringify(latest.current) !== savedKey.current) void save();
    }, [save]);

    /** Continue an existing draft; the state it was loaded with counts as saved. */
    const adopt = useCallback((id: string, loaded: Snapshot) => {
        idRef.current = id;
        setDraftId(id);
        savedKey.current = JSON.stringify(loaded);
        stopped.current = false;
        setStatus("saved");
        setSavedAt(null);
    }, []);

    /** Forget the draft (after discarding it) — the next change starts a new one. */
    const reset = useCallback((fresh: Snapshot) => {
        idRef.current = null;
        setDraftId(null);
        savedKey.current = JSON.stringify(fresh);
        stopped.current = false;
        setStatus("idle");
        setSavedAt(null);
    }, []);

    /** No more saves (the invoice is being created from this draft). `resume()` undoes it if creating fails. */
    const stop = useCallback(() => {
        stopped.current = true;
    }, []);

    /** After `stop()`: waits for a save still in flight and returns the draft id — a first save that
     *  was still running would otherwise create the draft after the invoice, leaving an orphan. */
    const settle = useCallback(async () => {
        await inFlightDone.current;
        return idRef.current;
    }, []);
    const resume = useCallback(() => {
        stopped.current = false;
    }, []);

    return { status, savedAt, draftId, retry: save, adopt, reset, stop, resume, settle };
}
